/* istanbul ignore file */
/**
 * Created by rathompson on 11/3/21
 * File: ProviderCard
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import React, { VFC, useCallback, useContext, useMemo, useState } from 'react'
import {
  useModal,
  Tab,
  TabList,
  TabPanel,
} from '@panwds/react-ui'
import {
  SUCCESS_VERIFIED,
  ERROR_VERIFY,
  ERROR_GENERIC,
  TAB_OWNERS,
  TAB_MAPPING,
  SUCCESS_DOWNLOAD, BUTTON_VIEW_INSTRUCTIONS, BUTTON_EDIT,
} from '../../../../constants/LabelConstants'
import OwnersTable from '../../table/OwnersTable'
import { ARRAY, OBJECT } from '../../../../../common/utils/Utils'
import AuthContext from '../../../../../common/context/AuthContext'
import { useMessage } from '../../../../../common/providers/MessageProvider'
import ConfigureIdpModal from '../../modal/ConfigureIdpModal'
import GlobalContext from '../../../../../common/context/GlobalContext'
import { IDP_OWNER_TYPES, IDP_OWNER_STATUS } from '../../../../constants/IAMConstants'
import DeleteIdentityFederationModal from '../../modal/DeleteIdentityFederationModal'
import { CreateFederationOwner, GetSpMetadata, UpdateFederationIdp } from '../../../../actions/IAMActions'
import type { I_TabItem } from '../../../common/types'
import ConfigurationBanner from './ConfigurationBanner'
import ProviderCardHeader from './ProviderCardHeader'
import FederationMappingTable from '../../table/FederationMappingTable'
import { ROOT_NODE_ID } from '../../../../../tenancy/constants/TenancyConstants'
import { getVarValue } from '../../../../../common/utils/Sparky'
import { FF_ENABLE_FEDERATION_MAPPING, FF_ENABLE_SP_CERT_DOWNLOAD } from '../../../../../common/utils/FeatureFlags'
import _ from 'lodash'
import FileDownload from 'js-file-download'
import styled from 'styled-components'
import DomainModal, { SIDEBAR_OPTIONS } from '../../modal/DomainModal'
import type { IAM_Federation } from '../../../../../common/reducers/types/IAMReducer.types'

// Feature Flags
const enableFederationMapping = getVarValue(FF_ENABLE_FEDERATION_MAPPING)
const enableSPCertDownload = getVarValue(FF_ENABLE_SP_CERT_DOWNLOAD)

export interface ISamlMetadata {
  identityProviderId: string,
  loginUrl: string,
  ssoUrl: string,
  certificate: string
}

interface I_Tabs { OWNERS: I_TabItem, MAPPING: I_TabItem }
export const IDP_TABS: I_Tabs = OBJECT.deepFreeze({
  OWNERS: { id: 'owners', label: TAB_OWNERS },
  MAPPING: { id: 'mapping', label: TAB_MAPPING }
})

function filterTSGHierarchy(node: any, tsgWhitelist: Array<any>): Array<any> {
  if (tsgWhitelist.includes(node.id)) {
    return [node]
  }
  if (!node.children) return []
  return node.children.reduce((accumulator: Array<any>, child: any) => {
    accumulator.push(...filterTSGHierarchy(child, tsgWhitelist))
    return accumulator
  }, [])
}

const CONTAINER = styled.div`
  div[role="tabpanel"] {
    margin: 0;
  }
  div[role="tab"] {
    z-index: 0;
  }
`

/**
 *   Provider details card
 */
const ProviderCard: VFC<{
  setTxtError: Function,
  userFederation: object,
  federation: IAM_Federation,
  federations: object,
  loading: boolean,
  fetchData: Function
}> = ({ userFederation = {}, federation = {}, federations = {}, loading = false, fetchData = () => null, setTxtError = () => null }) => {

  // Context
  const { user } = useContext(AuthContext)
  const { treeReducer, iamReducer } = useContext(GlobalContext)
  const { createBanner } = useMessage()

  // Reducers
  const { selectedTreeNode, sgKeyBy } = treeReducer
  const { federationMappingsMap } = iamReducer

  // State
  const [isActionsLoading, setIsActionsLoading] = useState(false)
  const [isStatusLoading, setIsStatusLoading] = useState(false)
  const [isVerifyLoading, setIsVerifyLoading] = useState<any>({})
  const [activeTabId, setActiveTabId] = useState<any>(IDP_TABS.OWNERS.id)

  // Modals
  const configureIdP: { openModal: Function } = useModal(ConfigureIdpModal)
  const deleteModal: { openModal: Function } = useModal(DeleteIdentityFederationModal)
  const domainModal: { openModal: Function } = useModal(DomainModal)

  const { owners, idps, domain, idpId, idp } = useMemo(() => {
    const { owners, idps, domain } = federation
    const idpId = idps?.[0]?.idpId
    const idp = idps?.[0]
    return {
      owners, idps, domain: domain || '', idpId, idp
    }
  }, [federation])

  const tsg_id = selectedTreeNode?.id
  const email = user?.email
  const owner = useMemo(() => owners?.find(o => o.user === email), [email, owners])
  const isVerified = useMemo(() => ([IDP_OWNER_STATUS.VERIFIED, IDP_OWNER_STATUS.WARNING] as any[]).includes(owner?.verification?.status), [owner])
  const verificationString = useMemo(() => _.get(owner, 'verification.verificationString', ''), [owner])
  const isFederationActive = useMemo(() => idps?.some(idp => idp.isActive), [idps])
  const onConfigureClick = useCallback(() => {
    configureIdP.openModal({
      domain,
      fetchData,
      federations,
      userFederation,
    })
  }, [configureIdP, fetchData, domain, userFederation, federations])

  const onHandleVerify = useCallback(async () => {
    if (tsg_id) {
      isVerifyLoading[email] = true
      setIsVerifyLoading(_.cloneDeep(isVerifyLoading))
      try {
        const { data: { verification: { status } } } = await CreateFederationOwner(tsg_id, domain, {
          user: email,
          verification: { type: IDP_OWNER_TYPES.DNS_TEXT }
        })
        if (([IDP_OWNER_STATUS.VERIFIED, IDP_OWNER_STATUS.WARNING] as any[]).includes(status)) {
          createBanner({ message: SUCCESS_VERIFIED, bannerProps: { appearance: 'success', type: 'page' } })
        }
        else {
          createBanner({ message: ERROR_VERIFY, bannerProps: { appearance: 'warning', type: 'page' } })
        }
      }
      catch (error: any) {
        createBanner({ message: error.message || ERROR_GENERIC })
      }
      isVerifyLoading[email] = false
      setIsVerifyLoading(_.cloneDeep(isVerifyLoading))
      await fetchData({ tsg_id, loading: false })
    }
  }, [createBanner, tsg_id, domain, fetchData, email, isVerifyLoading, setIsVerifyLoading])

  const onSPMetaClick = useCallback(async () => {
    if (tsg_id) {
      try {
        setIsActionsLoading(true)
        const { data: { metadata } } = await GetSpMetadata(tsg_id, domain)
        FileDownload(metadata, `${domain}.xml`)
        createBanner({ message: SUCCESS_DOWNLOAD, bannerProps: { appearance: 'success', type: 'page' } })
      }
      catch (error: any) {
        createBanner({ message: error.message || ERROR_GENERIC })
      }
      setIsActionsLoading(false)
    }
  }, [createBanner, domain, tsg_id])

  const onSPCertClick = useCallback(() => {

  }, [])

  const onIdpDeleteClick = useCallback(() => {
    deleteModal.openModal({
      domain,
      fetchData,
      idpId,
    })
  }, [deleteModal, domain, fetchData, idpId])

  const onIdpStatusChangeClick = useCallback(async () => {
    if (tsg_id) {
      setIsStatusLoading(true)
      try {
        const params = {
          isActive: !isFederationActive
        }
        await UpdateFederationIdp(tsg_id, domain, idpId, params)
        await fetchData({ tsg_id, loading: false, enableSuccessBanner: true })
      }
      catch (error: any) {
        createBanner({ message: error.message || ERROR_GENERIC })
      }
      setIsStatusLoading(false)
    }
  }, [createBanner, domain, fetchData, idpId, isFederationActive, tsg_id])

  const actions = useMemo(() => {
    const actions: Array<any> = []

    if (enableFederationMapping) {
      const downloadSPMetaAction = {
        id: 'download-sp-metadata',
        action: onSPMetaClick,
        value: 'Download SP Metadata'
      }

      const downloadSPCertAction = {
        id: 'download-sp-cert',
        action: onSPCertClick,
        value: 'Download SP Certificate'
      }

      const idpDeleteAction = {
        id: 'delete',
        value: idp ? 'Delete Identity Provider' : 'Delete',
        action: onIdpDeleteClick
      }

      const statusLabel = `${isFederationActive ? 'Disable' : 'Enable'} Identity Provider`
      const idpStatusAction = {
        id: 'status',
        value: statusLabel,
        action: onIdpStatusChangeClick,
      }

      const editAction = {
        id: 'edit',
        value: 'Edit Identity Provider',
        action: () => configureIdP.openModal({
          domain,
          fetchData,
          idps,
          idpId,
          isEdit: true,
          federations,
          userFederation,
        }),
      }

      actions.push(downloadSPMetaAction)
      if (enableSPCertDownload) {
        actions.push(downloadSPCertAction)
      }
      if (idp) {
        actions.push(idpStatusAction)
        actions.push(editAction)
        if (!idp.isActive) {
          actions.push(idpDeleteAction)
        }
      }
      else if (isVerified) {
        actions.push(idpDeleteAction)
      }
    }
    else {
      const viewInstructionsAction = {
        id: 'view-instructions',
        value: BUTTON_VIEW_INSTRUCTIONS,
        action: () => domainModal.openModal({
          federations,
          savedDomain: domain,
          step: SIDEBAR_OPTIONS.VERIFY,
          verificationString,
          shouldFetch: false
        }),
      }

      const deleteAction = {
        id: 'delete',
        action: onIdpDeleteClick,
        value: 'Delete'
      }
      const statusAction = {
        id: 'status',
        action: onIdpStatusChangeClick,
        value: isFederationActive ? 'Disable' : 'Enable'
      }
      const editAction = {
        id: 'edit',
        value: BUTTON_EDIT,
        action: () => configureIdP.openModal({
          domain,
          fetchData,
          idps,
          idpId,
          isEdit: true,
          federations,
          userFederation,
        }),
      }

      actions.push(viewInstructionsAction)
      actions.push(deleteAction)
      actions.push(statusAction)
      actions.push(editAction)
    }

    return actions
  }, [configureIdP, domain, domainModal, federations, fetchData, idp, idpId, idps, isFederationActive, isVerified, onIdpDeleteClick, onIdpStatusChangeClick, onSPCertClick, onSPMetaClick, userFederation, verificationString])

  const onActiveChange = useCallback(id => {
    setActiveTabId(id)
  }, [])

  const data: any[] = useMemo(() => {
    if (!isVerified) return []
    const tsgWhitelist = federationMappingsMap[domain]?.map(federationMapping => federationMapping.tsg_id) || []

    const children = filterTSGHierarchy(sgKeyBy[ROOT_NODE_ID], tsgWhitelist)
    const rootRow = {
      display_name: 'All Mapped Tenants',
      children
    }

    return children.length ? [rootRow] : [{
      display_name: 'No tenants have been mapped',
      children: []
    }]
  }, [isVerified, sgKeyBy, federationMappingsMap, domain])

  return (
    <CONTAINER className='tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-overflow-hidden'>
      <ProviderCardHeader
        activeTabId={activeTabId}
        actions={actions}
        isLoading={isActionsLoading}
        isStatusLoading={isStatusLoading}
        domain={domain}
        idp={idp}
        fetchData={fetchData}
        onSPMetaClick={onSPMetaClick}
        isVerified={isVerified}
      />
      { idps?.length === 0 && isVerified && <ConfigurationBanner {...{ onConfigureClick }}/>}
      <TabList activeTabId={activeTabId} onActiveChange={onActiveChange}>
        <Tab id={IDP_TABS.OWNERS.id}>{IDP_TABS.OWNERS.label}</Tab>
        {enableFederationMapping && isVerified && (
          <Tab id={IDP_TABS.MAPPING.id}>{IDP_TABS.MAPPING.label}</Tab>
        )}
      </TabList>
      {ARRAY.hasArray(owners) && (
        <TabPanel activeTabId={activeTabId} forTabId={IDP_TABS.OWNERS.id} addClassName='tw-h-full tw-p-4'>
          <OwnersTable
            setTxtError={setTxtError}
            federations={federations}
            loading={loading}
            verificationString={verificationString}
            fetchData={fetchData}
            isVerified={isVerified}
            domain={domain}
            owners={owners}
            email={email}
            isVerifyLoading={isVerifyLoading}
            onHandleVerify={onHandleVerify} />
        </TabPanel>
      )}
      {enableFederationMapping && isVerified && (
        <TabPanel activeTabId={activeTabId} forTabId={IDP_TABS.MAPPING.id} addClassName='tw-h-full tw-p-4 tw-overflow-y-scroll tw-overflow-x-hidden'>
          <FederationMappingTable data={data} border={false} domain={domain} style={{ flexGrow: 1, minHeight: '10rem', display: 'flex', flexDirection: 'column', maxHeight: '300px' }}/>
        </TabPanel>
      )}
    </CONTAINER>
  )
}

ProviderCard.propTypes = {}
export default ProviderCard
